/* @flow */

import * as React from 'react'
import styled from 'styled-components'

import {
  ControlLabel,
  EditorInput,
  FilesExistingValue,
  FormInput,
  FormGroup,
  RadioInput,
  SelectInput,
} from '../../../../../infrastructure/components/Formik'
import { useWebshopNavigationLinks } from '../../../../settings/hooks'
import { useWebshopPagesForSession } from '../../../hooks'
import {
  NAVIGATION_LINK_TYPE_SEARCH,
  NAVIGATION_LINK_TYPE_PAGE,
  NAVIGATION_LINK_TYPE_PAGE_V2,
  NAVIGATION_LINK_TYPE_BESTSELLERS,
} from '../../../shared'
import { defaultLinkButtonItem } from './shared'

const WebshopLinkButtonWidgetSettings = ({ brand, values, setFieldValue }) => {
  const [webshopNavigationLinks] = useWebshopNavigationLinks()
  const [webshopPages] = useWebshopPagesForSession(brand.id)

  const availableNavigationLinks = React.useMemo(() => {
    // Other types than search, page and bestsellers can be navigated to via URL,
    // so we offer only the few basic types
    return webshopNavigationLinks.filter(link =>
      [
        NAVIGATION_LINK_TYPE_SEARCH,
        NAVIGATION_LINK_TYPE_PAGE,
        NAVIGATION_LINK_TYPE_PAGE_V2,
        NAVIGATION_LINK_TYPE_BESTSELLERS,
      ].includes(link.type)
    )
  }, [webshopNavigationLinks])

  return (
    <div>
      <FormGroup>
        <ControlLabel required>Number of link buttons</ControlLabel>
        <FormInput
          name="data.number_of_link_buttons"
          type="number"
          min={1}
          step={1}
          onChange={e => {
            const numberOfLinkButtons = parseInt(e.target.value) || 0

            setFieldValue('data.number_of_link_buttons', numberOfLinkButtons)

            setFieldValue(
              'data.link_buttons',
              addOrRemoveListItems(
                values.data.link_buttons,
                numberOfLinkButtons,
                defaultLinkButtonItem
              )
            )
          }}
        />
      </FormGroup>

      <Separator />

      {values.data.link_buttons.map((linkButton, index) => (
        <React.Fragment key={index}>
          <FormGroup>
            <ControlLabel required>
              {values.data.link_buttons.length > 1 && <>#{index + 1}: </>}
              Button text
            </ControlLabel>
            <FormInput name={`data.link_buttons.${index}.button_text`} />
          </FormGroup>
          <FormGroup>
            <ControlLabel required>
              {values.data.link_buttons.length > 1 && <>#{index + 1}: </>}
              Link type
            </ControlLabel>
            <RadioInput
              id={`webshop_link_button_widget_settings_link_type_navigation_link_${index}`}
              name={`data.link_buttons.${index}.link_type`}
              label="Navigation link"
              value="navigation_link"
            />
            <RadioInput
              id={`webshop_link_button_widget_settings_link_type_webshop_page_${index}`}
              name={`data.link_buttons.${index}.link_type`}
              label="Webshop page"
              value="webshop_page"
            />
            <RadioInput
              id={`webshop_link_button_widget_settings_link_type_url_${index}`}
              name={`data.link_buttons.${index}.link_type`}
              label="Manual URL"
              value="manual_url"
            />
          </FormGroup>
          {linkButton.link_type === 'navigation_link' && (
            <FormGroup>
              <ControlLabel required>
                {values.data.link_buttons.length > 1 && <>#{index + 1}: </>}
                Navigation link
              </ControlLabel>
              <SelectInput
                name={`data.link_buttons.${index}.navigation_link_id`}
                options={availableNavigationLinks}
                simpleValue
                labelKey="text"
                valueKey="id"
              />
            </FormGroup>
          )}
          {linkButton.link_type === 'webshop_page' && (
            <FormGroup>
              <ControlLabel required>
                {values.data.link_buttons.length > 1 && <>#{index + 1}: </>}
                Webshop page
              </ControlLabel>
              <SelectInput
                name={`data.link_buttons.${index}.webshop_page_id`}
                options={webshopPages}
                simpleValue
                labelKey="heading"
                valueKey="id"
              />
            </FormGroup>
          )}
          {linkButton.link_type === 'manual_url' && (
            <FormGroup>
              <ControlLabel required>
                {values.data.link_buttons.length > 1 && <>#{index + 1}: </>}
                URL
              </ControlLabel>
              <FormInput name={`data.link_buttons.${index}.manual_url`} />
            </FormGroup>
          )}

          <Separator />
        </React.Fragment>
      ))}

      <FormGroup>
        <ControlLabel>Button(s) style</ControlLabel>
        <SelectInput
          labelKey="style_name"
          name="data.button_style"
          options={brand.webshop_design_settings.button_styles}
          simpleValue
          valueKey="id"
        />
      </FormGroup>
      <FormGroup>
        <ControlLabel>Button(s) size</ControlLabel>
        <SelectInput
          name="data.button_size"
          options={buttonSizeOptions}
          simpleValue
        />
      </FormGroup>
      {values.data.link_buttons.length > 1 && (
        <>
          <FormGroup>
            <ControlLabel required>Buttons stack-mode</ControlLabel>
            <RadioInput
              id="stack_mode_stacked"
              name="data.stack_mode"
              label="List buttons on top of each other"
              value="stacked"
            />
            <RadioInput
              id="stack_mode_side_by_side"
              name="data.stack_mode"
              label="List buttons side-by-side"
              value="side_by_side"
            />
          </FormGroup>
          <FormGroup>
            <ControlLabel required>
              Buttons space between each (px)
            </ControlLabel>
            <FormInput name="data.space_between_each" type="number" min={0} />
          </FormGroup>
        </>
      )}
    </div>
  )
}

export default WebshopLinkButtonWidgetSettings

const buttonSizeOptions = [
  { label: 'X-small', value: 'xsmall' },
  { label: 'Small', value: 'small' },
  { label: 'Normal', value: 'normal' },
  { label: 'Large', value: 'large' },
  { label: 'X-large', value: 'xlarge' },
]

const addOrRemoveListItems = (
  currentList: Array<Object>,
  targetItemsCount: number,
  newItem: Object
) => {
  let newList = currentList

  if (newList.length < targetItemsCount) {
    newList = [
      ...newList,
      ...Array.from({ length: targetItemsCount - newList.length }, () => ({
        ...newItem,
      })),
    ]
  } else if (newList.length > targetItemsCount) {
    newList = newList.slice(0, targetItemsCount)
  }

  return newList
}

const Separator = styled.hr`
  border: 0;
  margin: 20px 0;
`
